import React, { useState } from 'react';

import {
  OneThirdLayout,
  Flex,
  TwoThirdsLayout,
  InfoSection,
  EmptyStatePlaceholder,
  IconPanel,
  Button,
  Placeholder,
  useNotification,
  useModal
} from '@sopranodesign/pulse-components';
import { PermissionFilter } from '@sopranodesign/pulse-auth';
import { useCopyToClipboard } from '@sopranodesign/pulse-component-hooks';
import { useQuery, useQueryClient, useMutation } from '@sopranodesign/pulse-hooks';

import WebhookModal from './WebhookModal';
import {
  createWebhook,
  editWebhook,
  deleteWebhook as deleteWebhookApi
} from '../../api/index';

const Webhooks = () => {
  const [create, setCreate] = useState(false);

  const [edit, setEdit] = useState(null);

  const { showSuccess } = useNotification();

  const { copy, copied } = useCopyToClipboard();

  const { showModal, hideModal } = useModal();

  const query = useQuery({
    queryKey: [
      'webhooks'
    ]
  });

  const queryClient = useQueryClient();

  const createWebhookMutation = useMutation({
    mutationFn: createWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries('webhooks');
      showSuccess('Successfully added webhook');
      setCreate(false);
    }
  });

  const editWebhookMutation = useMutation({
    mutationFn: editWebhook,
    onSuccess: () => {
      queryClient.invalidateQueries('webhooks');
      showSuccess('Successfully updated webhook');
      setEdit(null);
    }
  });

  const { mutate: deleteWebhook } = useMutation({
    mutationFn: deleteWebhookApi,
    onSuccess: () => {
      queryClient.invalidateQueries('webhooks');
      showSuccess('Successfully deleted webhook');
    }
  });

  const handleCreateWebhook = form => {
    createWebhookMutation.mutate(form);
  };

  const handleEditWebhook = form => {
    editWebhookMutation.mutate({ id: edit.id, ...form });
  };

  const handleDeleteWebhook = webhook => {
    showModal({
      header: `Confirm deletion of ${webhook.name} webhook`,
      body: `Are you sure you want to delete this webhook for ${webhook.referring_domain}?`,
      handleConfirm: () => {
        try {
          deleteWebhook(webhook.id);
        } finally {
          hideModal();
        }
      }
    });
  };

  return (
    <Flex>
      {create && (
        <WebhookModal
          handleClose={() => setCreate(false)}
          handleSubmit={handleCreateWebhook}
          loading={createWebhookMutation.isPending}
        />
      )}
      {edit && (
        <WebhookModal
          handleClose={() => setEdit(null)}
          handleSubmit={handleEditWebhook}
          loading={editWebhookMutation.isPending}
          initialWebhook={{
            name: edit.name,
            referring_domain: edit.referring_domain,
            webhook_action_type_id: edit.webhook_action_type_id,
            webhook_action_type_name: edit.webhook_action_type_name,
            variable_id: edit.variable_id
          }}
        />
      )}
      <OneThirdLayout>
        <InfoSection
          title="Webhooks"
          info="Trigger your chatbot with incoming webhooks."
          actions={
            query.isSuccess &&
            query.data.data.length !== 0 && (
              <PermissionFilter can="edit webhooks">
                <Button
                  icon="plus"
                  variant="primary"
                  onClick={() => setCreate(true)}>
                  New Webhook Integration
                </Button>
              </PermissionFilter>
            )
          }
        />
      </OneThirdLayout>
      <TwoThirdsLayout>
        {query.isLoading && <Placeholder />}
        {query.isSuccess && query.data.data.length === 0 && (
          <EmptyStatePlaceholder
            type="integrations"
            heading="Create your first webhook integration"
            text="Trigger automation from third-party with webhooks."
            actions={
              <PermissionFilter can="edit webhooks">
                <Button
                  icon="plus"
                  variant="primary"
                  onClick={() => setCreate(true)}>
                  New Webhook Integration
                </Button>
              </PermissionFilter>
            }
            helpLink="/docs"
            helpText="Learn more about webhook integrations."
          />
        )}
        {query.isSuccess &&
          query.data.data.length > 0 &&
          query.data.data.map(webhook => (
            <Flex col ySpace mb key={webhook.id}>
              <IconPanel
                icon={'globe'}
                title={webhook.name}
                subtitle={webhook.webhook_action_type_name}
                status={`${webhook.webhook_url}?key=${webhook.key}`}
                actions={
                  <PermissionFilter can="edit webhooks">
                    <Flex xSpace>
                      <Button
                        data-testid={`edit ${webhook.name}`}
                        onClick={() => setEdit(webhook)}
                        icon="pencilAlt">
                        Edit
                      </Button>
                      <Button
                        variant="secondary"
                        icon="clipboardCopy"
                        onClick={() => {
                          copy(`${webhook.webhook_url}?key=${webhook.key}`);
                        }}>
                        {copied ? 'Copied' : 'Copy'}
                      </Button>
                      <Button
                        data-testid={`delete ${webhook.name}`}
                        variant="secondary"
                        icon="trash"
                        colour="danger"
                        loading={deleteWebhook.isLoading}
                        onClick={() => handleDeleteWebhook(webhook)}>
                        Delete
                      </Button>
                    </Flex>
                  </PermissionFilter>
                }
              />
            </Flex>
          ))}
      </TwoThirdsLayout>
    </Flex>
  );
};

export default Webhooks;
